import { Ban, CheckCircle2, History, Settings } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ErrorState } from '../../../../components/ErrorState';
import { Menu } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { LearningDevelopmentModal } from '../components/LearningDevelopmentModal';
import { learningDevelopmentApi } from '../learningDevelopmentApi';
import type {
  LearningDevelopmentCatalog,
  LearningDevelopmentEntry,
} from '../learningDevelopmentTypes';

type TabKey = 'info';

/**
 * One row of one L&D vocabulary.
 *
 * On the **header** layout rather than the sidebar one: a catalog row reads as a single
 * continuous thing — what it covers, where it sits, and the trail of who changed the wording —
 * not a record whose content splits into panels a reader chooses between.
 *
 * It earns its keep on the count: how many L&D records are already filed under a row is what a
 * reader wants before rewording it or retiring it.
 */
export function LearningDevelopmentProfilePage({
  catalog,
}: {
  catalog: LearningDevelopmentCatalog;
}) {
  const { id } = useParams<{ id: string }>();

  const [entry, setEntry] = useState<LearningDevelopmentEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      learningDevelopmentApi
        .get(catalog.slug, id, signal)
        .then((next) => {
          if (!signal?.aborted) setEntry(next);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError
              ? caught
              : new ApiError(`Could not load this ${catalog.noun}.`, 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [catalog.noun, catalog.slug, id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  // A plain string is a segment that goes nowhere; the two links are the way back.
  const breadcrumb: BreadcrumbSegment[] = [
    'Human Resources',
    'Personnel',
    { label: 'Settings', to: '/personnel/settings' },
    { label: catalog.label, to: catalog.settingsPath },
    entry ? entry.name : catalog.label,
  ];

  if (error) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading || !entry) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const Icon = catalog.icon;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="ld-entry-profile"
        header={
          <ProfileHeader
            id="ld-entry-profile-header"
            icon={<Icon className="h-6 w-6" aria-hidden="true" />}
            title={entry.name}
            meta={[
              // Unlabelled, because "12 L&D records" says what it is on its own.
              `${entry.trainingCount} L&D ${entry.trainingCount === 1 ? 'record' : 'records'}`,
            ]}
            // Says WHICH vocabulary this row belongs to, which the name alone cannot: several
            // of these words legitimately appear in more than one of the four.
            secondary={[catalog.label]}
            status={
              <StatusText tone={entry.isActive ? 'active' : 'inactive'}>
                {entry.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={entry.createdByName}
            createdAt={entry.createdAt}
            /*
              The record-scoped history, in the header cog and scoped with `entityId` — where
              CLAUDE.md puts it for a profile. Its key is the audit module's, not this one's: a
              control carries the key of what it opens.
            */
            actions={
              <Menu
                id="ld-entry-profile-settings"
                items={[
                  {
                    key: 'history',
                    label: 'History Logs',
                    icon: <History className="h-4 w-4" aria-hidden="true" />,
                    onClick: () => setHistoryOpen(true),
                    permission: 'audit-logs.read',
                  },
                ]}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label={`${catalog.noun} settings`}
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          entry.isActive ? (
            <ProfileBanner
              id="ld-entry-profile-banner"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message={`This ${catalog.noun} is offered: it appears on the Training/L&D form of every personnel record.`}
            />
          ) : (
            <ProfileBanner
              id="ld-entry-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={`This ${catalog.noun} is deleted. It is no longer offered, and the records already filed under it keep it.`}
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="ld-entry-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'learning-development.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: catalog.columnHeader, value: entry.name },
              { label: 'Vocabulary', value: catalog.label },
              { label: 'Description', value: entry.description },
              {
                label: 'Order',
                value: <span className="font-mono tabular-nums">{entry.sortOrder}</span>,
              },
              {
                label: 'L&D records',
                value: <span className="font-mono tabular-nums">{entry.trainingCount}</span>,
              },
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="ld-entry-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: entry.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(entry.createdAt)} className="font-mono">
                    {formatDateTime(entry.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: entry.updatedByName },
              {
                label: 'Updated',
                value: entry.updatedAt ? (
                  <time dateTime={toIso(entry.updatedAt)} className="font-mono">
                    {formatDateTime(entry.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <LearningDevelopmentModal
          catalog={catalog}
          open
          editing={entry}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="learning-development"
        entityTypes={[ENTITY_TYPES[catalog.slug]]}
        entityId={entry.id}
      />
    </div>
  );
}

/** The audit entity behind each slug — four tables, four CLR names. */
const ENTITY_TYPES: Record<LearningDevelopmentCatalog['slug'], string> = {
  types: 'LearningDevelopmentType',
  categories: 'LearningDevelopmentCategory',
  interventions: 'DevelopmentIntervention',
  sponsors: 'TrainingSponsor',
};
